import { Link } from "react-router";
import {
	CreditCard,
	ArrowDownToLine,
	User,
	LogOut,
	Sun,
	Moon,
	DollarSign,
	Plus,
	Headset,
	Gift,
} from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/com/ui/dropdown-menu";
import { DepositModal } from "@/com/shared/DepositModal/DepositModal";
import { cn } from "@/lib/utils";
import { TenantBrand } from "@/com/ui/TenantBrand/TenantBrand";
import { tenant } from "@/lib/tenant";

interface Language {
	code: string;
	label: string;
}

interface TopNavbarComponentProps {
	lang: string;
	onLangChange: (code: string) => void;
	languages: Language[];
	isDark: boolean;
	onToggleTheme: () => void;
	canToggleTheme?: boolean;
	depositOpen: boolean;
	onDepositOpenChange: (open: boolean) => void;
	onLogout: () => void;
	mockUsdBalance: number;
	cardsBalanceFormatted: string;
	usdBalanceFormatted: string;
}

export function TopNavbarComponent({
	lang,
	onLangChange,
	languages,
	isDark,
	onToggleTheme,
	canToggleTheme = true,
	depositOpen,
	onDepositOpenChange,
	onLogout,
	cardsBalanceFormatted,
	usdBalanceFormatted,
}: TopNavbarComponentProps) {
	// Dark navbar pill — gives white-lettered tenant logos a proper stage
	const dark = !!tenant.darkNav;

	return (
		<header className="hidden md:block">
			<div className="container mx-auto px-4 max-w-6xl py-3">
				<div
					className={cn(
						"flex items-center justify-between gap-4 backdrop-blur-xl rounded-full px-4 py-2.5 border shadow-[0_8px_32px_rgba(0,0,0,0.08)]",
						dark
							? "bg-[#14181F] border-white/10"
							: "bg-card border-border",
					)}
				>
					{/* Left: Logo */}
					<Link to="/" className="flex items-center gap-2 shrink-0">
						<TenantBrand size="md" />
					</Link>

					{/* Right: Balance pill + New Card + Theme + Profile */}
					<div className="flex items-center gap-2">
						{/* Dual balance pill + Deposit */}
						<div className={cn("flex items-center rounded-full h-9", dark ? "bg-white/10" : "bg-foreground")}>
							{/* Available USD */}
							<div className="flex items-center gap-1.5 pl-3 pr-2.5">
								<DollarSign className={cn("w-3 h-3", dark ? "text-white/50" : "text-background/50")} />
								<span className={cn("text-xs font-mono font-semibold", dark ? "text-white" : "text-background")}>
									{usdBalanceFormatted}
								</span>
							</div>

							<div className={cn("w-px h-4", dark ? "bg-white/20" : "bg-background/20")} />

							{/* Cards total */}
							<div className="flex items-center gap-1.5 pl-2.5 pr-2.5">
								<CreditCard className={cn("w-3 h-3", dark ? "text-white/50" : "text-background/50")} />
								<span className={cn("text-xs font-mono font-semibold", dark ? "text-white" : "text-background")}>
									{cardsBalanceFormatted}
								</span>
							</div>

							<div className={cn("w-px h-4", dark ? "bg-white/20" : "bg-background/20")} />

							{/* Deposit button */}
							<button
								onClick={() => onDepositOpenChange(true)}
								className={cn("flex items-center gap-1 pl-2.5 pr-3 h-full rounded-r-full transition-colors", dark ? "hover:bg-white/10" : "hover:bg-background/10")}
							>
								<ArrowDownToLine className={cn("w-3 h-3", dark ? "text-white/80" : "text-background/80")} />
								<span className={cn("text-xs font-semibold", dark ? "text-white" : "text-background")}>
									Deposit
								</span>
							</button>
						</div>

						{/* New Card CTA */}
						<Link
							to="/order-card"
							className={cn("flex items-center gap-1.5 h-9 px-4 rounded-full border text-sm font-semibold transition-colors", dark ? "border-white/15 text-white hover:bg-white/10" : "border-border hover:bg-foreground/5")}
						>
							<Plus className="w-3.5 h-3.5" />
							New Card
						</Link>

						{/* Support link — only when tenant provides a Telegram contact */}
						{tenant.telegramSupport && (
							<a
								href={tenant.telegramSupport}
								target="_blank"
								rel="noopener noreferrer"
								className={cn("group flex items-center gap-1.5 h-9 px-4 rounded-full border text-sm font-semibold transition-colors", dark ? "border-white/15 text-white hover:bg-white/10" : "border-border hover:bg-foreground/5")}
							>
								<Headset className="w-4 h-4 transition-transform group-hover:-rotate-6" />
								Support
							</a>
						)}

						{/* Theme toggle (hidden when tenant locks the theme) */}
						{canToggleTheme && (
							<button
								onClick={onToggleTheme}
								className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors", dark ? "bg-white/10 hover:bg-white/20" : "bg-foreground/8 hover:bg-foreground/15")}
							>
								{isDark ? (
									<Sun className={cn("w-4 h-4", dark ? "text-white/70" : "text-muted-foreground")} />
								) : (
									<Moon className={cn("w-4 h-4", dark ? "text-white/70" : "text-muted-foreground")} />
								)}
							</button>
						)}

						{/* Profile dropdown — includes Language, Referral, Settings */}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors", dark ? "bg-white/10 hover:bg-white/20" : "bg-foreground/8 hover:bg-foreground/15")}>
									<User className={cn("w-4 h-4", dark ? "text-white/70" : "text-muted-foreground")} />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-48 bg-background">
								<DropdownMenuItem asChild>
									<Link to="/profile">
										<User className="w-4 h-4 mr-2" />
										Profile
									</Link>
								</DropdownMenuItem>
								<DropdownMenuItem asChild>
									<Link to="/referral">
										<Gift className="w-4 h-4 mr-2" />
										Refer &amp; Earn
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem className="text-destructive" onClick={onLogout}>
									<LogOut className="w-4 h-4 mr-2" />
									Log out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>

			<DepositModal open={depositOpen} onOpenChange={onDepositOpenChange} />
		</header>
	);
}
